require('./style.scss');
import { describeResult } from './geometry';
import { LAYOUT_MODELS, detectLayout, absolutePanoramaUrl } from './api';

/**
 * "Detect room" panel in the admin floor editor: the admin picks a model and
 * presses "Draw" — only that model runs on the panorama and its room is drawn
 * straight into the floor editor, finished, exactly like a hand-drawn room
 * after its last corner (no corners are added by clicking). Picking another
 * model and "Draw" again replaces it; switching back to a drawn model redraws
 * it instantly. "Draw by hand" hands over to the normal manual drawing.
 *
 *   new DetectRoomPanel({ container, panorama, onApply, onDrawManually, onClose, onDecision })
 *
 * onApply(layout)      — draw this layout as the room (same format as image layout_json)
 * onDrawManually()     — clear the room and start the manual drawing
 * onClose()            — panel closed with Close / ×
 * onDecision(record)   — what was tried and what was kept (training data, saved later)
 */
export default class DetectRoomPanel {
  constructor({ container, panorama, onApply, onDrawManually, onClose, onDecision, detect = detectLayout, pageUrl = window.location.href }) {
    this._container = container;
    this._imageUrl = absolutePanoramaUrl(panorama, pageUrl);
    this._onApply = onApply || (() => {});
    this._onDrawManually = onDrawManually || (() => {});
    this._onClose = onClose || (() => {});
    this._onDecision = onDecision || (() => {});
    this._detect = detect;
    this._results = {};
    this._pending = new Set();
    this._selected = LAYOUT_MODELS[0].id;
    this._applied = null;
    this._closed = false;
    this._build();
  }

  _build() {
    const el = document.createElement('div');
    el.className = 'e3-detect-room';
    el.innerHTML = `
      <div class="e3-detect-room__head">
        <strong>Detect room</strong>
        <button type="button" class="e3-detect-room__close" aria-label="Close">×</button>
      </div>
      <div class="e3-detect-room__models"></div>
      <button type="button" class="e3-detect-room__run" data-act="run">Draw</button>
      <div class="e3-detect-room__status" role="status"></div>
      <div class="e3-detect-room__actions">
        <button type="button" data-act="draw">Draw by hand</button>
        <button type="button" data-act="close">Close</button>
      </div>
      <div class="e3-detect-room__hint">Pick a model and press “Draw” — the room is drawn as finished. Try other models to compare; the last one drawn is kept. To draw it yourself, press “Draw by hand”.</div>`;
    const models = el.querySelector('.e3-detect-room__models');
    LAYOUT_MODELS.forEach(({ id, label, hint }) => {
      const row = document.createElement('label');
      row.className = 'e3-detect-room__model';
      row.innerHTML = `<input type="radio" name="e3-detect-model" value="${id}"> <span>${label}</span> <em data-model="${id}"></em>`;
      if (hint) row.title = hint;
      const input = row.querySelector('input');
      input.checked = id === this._selected;
      input.addEventListener('change', () => this._select(id));
      models.appendChild(row);
    });
    el.querySelector('.e3-detect-room__close').addEventListener('click', () => this.close());
    el.querySelector('[data-act="close"]').addEventListener('click', () => this.close());
    el.querySelector('[data-act="run"]').addEventListener('click', () => this._run(this._selected));
    el.querySelector('[data-act="draw"]').addEventListener('click', () => {
      this._record('manual');
      this._remove();
      this._onDrawManually();
    });
    this._el = el;
    this._status = el.querySelector('.e3-detect-room__status');
    this._runBtn = el.querySelector('[data-act="run"]');
  }

  open() {
    this._container.appendChild(this._el);
    this._select(this._selected);
  }

  // Runs one model on the panorama; other models can be picked (or run) meanwhile.
  async _run(id) {
    if (!this._imageUrl || this._pending.has(id) || id in this._results) return;
    this._pending.add(id);
    this._setTag(id, 'running…');
    this._select(id);
    let result;
    try {
      const res = await this._detect(this._imageUrl, [id]);
      result = (res && res[id]) || { error: 'no result' };
    } catch (e) {
      result = { error: e.message };
    }
    this._pending.delete(id);
    if (this._closed) return;
    this._results[id] = result;
    this._setTag(id, result.error ? 'failed' : describeResult(result));
    if (id === this._selected) this._select(id);
  }

  _usable(id) {
    const r = this._results[id];
    return !!(r && r.uv && r.uv.length >= 6);
  }

  _label(id) {
    return LAYOUT_MODELS.find(m => m.id === id)?.label || id;
  }

  _setTag(id, text) {
    const tag = this._el.querySelector(`[data-model="${id}"]`);
    if (tag) tag.textContent = text;
  }

  // Selecting a model that has a result draws it as the room (if it isn't already).
  _select(id) {
    this._selected = id;
    const input = this._el.querySelector(`input[value="${id}"]`);
    if (input) input.checked = true;
    const result = this._results[id];
    const running = this._pending.has(id);
    this._runBtn.disabled = !this._imageUrl || running || id in this._results;
    this._runBtn.textContent = running ? 'Drawing…' : 'Draw';
    const label = this._label(id);
    if (this._usable(id) && this._applied !== id) {
      this._applied = id;
      this._onApply(result);
    }
    if (!this._imageUrl) {
      this._setStatus('This panorama has no image URL the models can read. You can still draw the room yourself.');
    } else if (running) {
      this._setStatus(`${label}: detecting the room${id === 'consensus' ? ' (runs every detector — about 15 seconds)' : ' — a few seconds'}…`);
    } else if (!result) {
      this._setStatus(`${label}: press “Draw” to draw its room.`);
    } else if (this._usable(id)) {
      this._setStatus(`${label}: ${describeResult(result)} — drawn.`);
    } else {
      this._setStatus(`${label}: ${result.error ? `failed (${result.error})` : 'no usable result'}. Try another model or draw the room yourself.`);
    }
  }

  _setStatus(text) {
    this._status.textContent = text;
  }

  _record(chosen) {
    if (this._recorded) return;
    this._recorded = true;
    this._onDecision({
      image_url: this._imageUrl,
      chosen,
      results: this._results,
      at: new Date().toISOString(),
    });
  }

  _remove() {
    this._closed = true;
    if (this._el.parentNode) this._el.parentNode.removeChild(this._el);
  }

  // Close / × (or the floor editor closing): the last drawn room stays.
  close() {
    if (this._closed) return;
    if (this._applied || Object.keys(this._results).length) this._record(this._applied || 'none');
    this._remove();
    this._onClose();
  }
}
